/**
 * Collision grid: world-pixel queries over the map's `collision` array (0 walkable, 1 solid).
 * Phaser-free. Out-of-bounds is always solid so actors can never leave the map.
 *
 * Conventions (ARCHITECTURE.md §1): an actor's position is its feet baseline `(feetX, feetY)`;
 * the sprite is drawn with origin (0.5, 1), so the bottom pixel row of the sprite is `feetY - 1`.
 * The collision body is a `w × h` box whose bottom edge sits on the feet baseline:
 * `[feetX - w/2, feetX + w/2) × [feetY - h, feetY)`.
 */
import { TILE } from '@/core/config';
import type { MapData } from '@/core/types/contracts';
import type { Rect } from './queries';

export interface BodySize { w: number; h: number }
/** Default map-character body from the architecture contract (20 × 12 at the feet). */
export const DEFAULT_BODY: BodySize = { w: 20, h: 12 };

export class CollisionGrid {
  private readonly solid: Uint8Array;

  constructor(readonly width: number, readonly height: number, collision: ArrayLike<number>, readonly tileSize: number = TILE) {
    this.solid = new Uint8Array(Math.max(0, width * height));
    for (let i = 0; i < this.solid.length; i++) this.solid[i] = (collision[i] ?? 1) !== 0 ? 1 : 0;
  }

  static fromMap(data: MapData): CollisionGrid {
    return new CollisionGrid(data.width, data.height, data.collision, data.tileSize);
  }

  get worldWidth(): number { return this.width * this.tileSize; }
  get worldHeight(): number { return this.height * this.tileSize; }

  inBounds(tx: number, ty: number): boolean {
    return tx >= 0 && ty >= 0 && tx < this.width && ty < this.height;
  }

  /** Tile query; outside the map counts as solid. */
  isSolidTile(tx: number, ty: number): boolean {
    if (!this.inBounds(tx, ty)) return true;
    return this.solid[ty * this.width + tx] === 1;
  }

  /** Runtime override (e.g. a gate opening). Does not mutate the source MapData. */
  setSolid(tx: number, ty: number, solid: boolean): void {
    if (!this.inBounds(tx, ty)) return;
    this.solid[ty * this.width + tx] = solid ? 1 : 0;
  }

  tileAt(px: number, py: number): { tx: number; ty: number } {
    return { tx: Math.floor(px / this.tileSize), ty: Math.floor(py / this.tileSize) };
  }

  /** Point query in world pixels. */
  isBlocked(px: number, py: number): boolean {
    const { tx, ty } = this.tileAt(px, py);
    return this.isSolidTile(tx, ty);
  }

  /** True if any tile overlapped by the half-open rect is solid. */
  isBlockedRect(x: number, y: number, w: number, h: number): boolean {
    if (w <= 0 || h <= 0) return this.isBlocked(x, y);
    const tx0 = Math.floor(x / this.tileSize), ty0 = Math.floor(y / this.tileSize);
    const tx1 = Math.floor((x + w - 1) / this.tileSize), ty1 = Math.floor((y + h - 1) / this.tileSize);
    for (let ty = ty0; ty <= ty1; ty++) for (let tx = tx0; tx <= tx1; tx++) if (this.isSolidTile(tx, ty)) return true;
    return false;
  }

  bodyRect(feetX: number, feetY: number, body: BodySize = DEFAULT_BODY): Rect {
    return { x: feetX - Math.floor(body.w / 2), y: feetY - body.h, w: body.w, h: body.h };
  }

  /** Feet-anchored body query (see file header for the body convention). */
  isBodyBlocked(feetX: number, feetY: number, body: BodySize = DEFAULT_BODY): boolean {
    const r = this.bodyRect(feetX, feetY, body);
    return this.isBlockedRect(r.x, r.y, r.w, r.h);
  }

  /** Copy of the current grid (0/1 per tile, row-major). */
  toArray(): number[] { return Array.from(this.solid); }
}

export interface SlideResult { x: number; y: number; blockedX: boolean; blockedY: boolean }

/**
 * Axis-separated integer slide move: advances pixel by pixel on X then Y so a body can never
 * tunnel through a tile, and slides along walls when moving diagonally. Deltas are rounded to
 * integers (callers accumulate sub-pixel speed themselves).
 */
export function slideMove(grid: CollisionGrid, feetX: number, feetY: number, dx: number, dy: number, body: BodySize = DEFAULT_BODY): SlideResult {
  let cx = Math.round(feetX), cy = Math.round(feetY);
  const sx = Math.sign(dx), sy = Math.sign(dy);
  let remX = Math.abs(Math.round(dx)), remY = Math.abs(Math.round(dy));
  let blockedX = false, blockedY = false;
  while (remX > 0) {
    if (grid.isBodyBlocked(cx + sx, cy, body)) { blockedX = true; break; }
    cx += sx; remX--;
  }
  while (remY > 0) {
    if (grid.isBodyBlocked(cx, cy + sy, body)) { blockedY = true; break; }
    cy += sy; remY--;
  }
  return { x: cx, y: cy, blockedX, blockedY };
}
